import React, { useState } from "react";
import services from "../data/services";
import ServiceModal from "./ServiceModal";
import useReveal from "../hooks/useReveal";

function ServiceCard({ service, index, onOpen }) {
  const ref = useReveal();
  // First and last cards run double-width so the 7 services fill three clean rows
  const wide = index === 0 || index === services.length - 1;

  return (
    <button
      ref={ref}
      type="button"
      onClick={() => onOpen(service)}
      style={{ "--reveal-delay": `${(index % 3) * 110}ms` }}
      className={`group reveal relative flex flex-col overflow-hidden border border-white/10 bg-ink-900 text-left
                  transition-colors duration-500 hover:border-flame-500/50 ${
                    wide ? "lg:col-span-2" : ""
                  }`}
      aria-label={`${service.title} — view details`}
    >
      {/* Orange rule that sweeps across the top edge on hover */}
      <span className="absolute inset-x-0 top-0 z-20 h-[2px] origin-left scale-x-0 bg-flame-500 transition-transform duration-500 group-hover:scale-x-100" />

      {/* Image */}
      <div
        className={`relative overflow-hidden ${
          wide ? "aspect-[16/9] lg:aspect-[2.4/1]" : "aspect-[4/3]"
        }`}
      >
        <img
          src={service.image}
          alt={service.title}
          loading="lazy"
          className="h-full w-full object-cover opacity-70 saturate-[.7] transition-all duration-[900ms] ease-out group-hover:scale-105 group-hover:opacity-100 group-hover:saturate-100"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/35 to-transparent" />

        {/* Plate number */}
        <span className="absolute left-5 top-4 font-mono text-[11px] tracking-wide2 text-flame-500">
          {service.num}
        </span>

        {/* Technical corner brackets */}
        <span className="bracket absolute inset-4" />
      </div>

      {/* Copy */}
      <div className="relative flex flex-1 flex-col p-6 lg:p-7">
        <p className="mb-2 font-mono text-[10px] uppercase tracking-kicker text-steel-400">
          {service.tagline}
        </p>
        <h3
          className={`h-display leading-[0.95] transition-colors duration-300 group-hover:text-flame-500 ${
            wide ? "text-3xl lg:text-4xl" : "text-2xl lg:text-[1.75rem]"
          }`}
        >
          {service.title}
        </h3>
        <p className="mt-3 line-clamp-3 text-sm text-steel-400">
          {service.description}
        </p>

        <span className="mt-6 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wide2 text-white">
          View detail
          <span className="inline-block transition-transform duration-300 group-hover:translate-x-1.5">
            →
          </span>
        </span>
      </div>
    </button>
  );
}

export default function Products() {
  const [selected, setSelected] = useState(null);
  const headRef = useReveal();

  return (
    <section
      id="capabilities"
      className="relative overflow-hidden bg-ink-900 py-24 lg:py-32"
    >
      <div className="absolute inset-0 bg-grid bg-grid opacity-25" />

      <div className="relative mx-auto max-w-shell px-6 lg:px-10">
        <div
          ref={headRef}
          className="reveal mb-14 flex flex-col gap-6 lg:mb-20 lg:flex-row lg:items-end lg:justify-between"
        >
          <div>
            <p className="kicker mb-6 max-w-xs">02 — Capabilities</p>
            <h2 className="h-display text-5xl sm:text-6xl lg:text-7xl">
              Products
              <br />& <span className="text-flame-500">services</span>
            </h2>
          </div>
          <p className="max-w-md text-steel-400">
            Seven service lines run out of our South Oropouche facility — from
            downhole recovery and section milling to certified fabrication and
            in-house chart torquing.
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((service, i) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={i}
              onOpen={setSelected}
            />
          ))}
        </div>
      </div>

      {selected && (
        <ServiceModal service={selected} onClose={() => setSelected(null)} />
      )}
    </section>
  );
}
